/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-empty-function */
import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  forwardRef,
  HostBinding,
  Input,
  Output
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { UmssCheckboxComponent } from '../checkbox/checkbox.component';

// Interface for rich checkbox options with primary and secondary text
export interface RichCheckboxGroupOption<T = any> {
  label: string; // Primary label text
  secondaryText?: string; // Secondary descriptive text
  value: T; // Value for form submission
  id?: string; // Optional unique identifier
  disabled?: boolean; // Individual option disabled state
}

@Component({
  selector: 'umss-rich-checkbox-group',
  standalone: true,
  imports: [CommonModule, UmssCheckboxComponent],
  templateUrl: './rich-checkbox-group.component.html',
  styleUrl: './rich-checkbox-group.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UmssRichCheckboxGroupComponent),
      multi: true
    }
  ]
})
export class UmssRichCheckboxGroupComponent<T> implements ControlValueAccessor {
  @HostBinding('style.display') display = 'contents';

  private _generatedId = `rich-checkbox-group-${Math.random().toString(36).substr(2, 9)}`;

  constructor() {}

  /** Content props */
  @Input() label?: string; // Group label
  @Input() options: RichCheckboxGroupOption<T>[] = []; // Array of rich checkbox options
  @Input() name?: string; // Form name attribute

  /** Variant props */
  @Input() size: 'Small' | 'Regular' | 'Large' = 'Regular'; // Size variant
  @Input() variant: 'Default' | 'Card' = 'Default'; // Visual variant for the group container
  @Input() layout: 'Vertical' | 'Horizontal' = 'Vertical'; // Group layout variant

  /** State props */
  @Input() selectedValues: T[] = []; // Array of selected values
  @Input() disabled: boolean = false; // Disabled state for entire group
  @Input() required: boolean = false; // Required field indicator
  @Input() error: boolean = false; // Error state
  @Input() maxSelections?: number; // Maximum number of selections allowed
  @Input() minSelections?: number; // Minimum number of selections required

  /** Event outputs */
  @Output() selectedValuesChange = new EventEmitter<T[]>();
  @Output() optionChange = new EventEmitter<{
    option: RichCheckboxGroupOption;
    checked: boolean;
  }>();

  /** Internal state */
  private _selectedValues: T[] = [];
  private onChange = (value: T[]) => {};
  private onTouched = () => {};

  /** Computed properties */
  get computedId(): string {
    return this._generatedId;
  }

  get hasError(): boolean {
    if (this.error) return true;
    if (this.minSelections && this._selectedValues.length < this.minSelections)
      return true;
    if (this.maxSelections && this._selectedValues.length > this.maxSelections)
      return true;
    return false;
  }

  get canSelectMore(): boolean {
    if (!this.maxSelections) return true;
    return this._selectedValues.length < this.maxSelections;
  }

  get errorMessage(): string {
    if (
      this.minSelections &&
      this._selectedValues.length < this.minSelections
    ) {
      return `Please select at least ${this.minSelections} option${this.minSelections > 1 ? 's' : ''}.`;
    }
    if (
      this.maxSelections &&
      this._selectedValues.length > this.maxSelections
    ) {
      return `Please select no more than ${this.maxSelections} option${this.maxSelections > 1 ? 's' : ''}.`;
    }
    return '';
  }

  /** Helper methods */
  isSelected(value: T): boolean {
    return this._selectedValues.includes(value);
  }

  trackByValue(index: number, option: RichCheckboxGroupOption): unknown {
    return option.value;
  }

  /** Event handlers */
  onCheckboxChange(option: RichCheckboxGroupOption, checked: boolean): void {
    if (this.disabled || option.disabled) return;

    let newSelectedValues = [...this._selectedValues];

    if (checked) {
      // Check if we can add more selections
      if (
        this.maxSelections &&
        newSelectedValues.length >= this.maxSelections
      ) {
        return; // Don't allow more selections
      }
      if (!newSelectedValues.includes(option.value)) {
        newSelectedValues.push(option.value);
      }
    } else {
      newSelectedValues = newSelectedValues.filter(
        (val) => val !== option.value
      );
    }

    this.updateSelectedValues(newSelectedValues);
    this.optionChange.emit({ option, checked });
  }

  private updateSelectedValues(values: T[]): void {
    this._selectedValues = values;
    this.selectedValues = values;
    this.selectedValuesChange.emit(values);
    this.onChange(values);
    this.onTouched();
  }

  /** ControlValueAccessor implementation */
  writeValue(value: T[]): void {
    this._selectedValues = value || [];
    this.selectedValues = this._selectedValues;
  }

  registerOnChange(fn: (value: unknown[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
